/**
 * Comparación de mercado (precio vs. mercado de una unidad): flag del Front y lectura de errores.
 * Contrato: argenlotapi `docs/frontend/FRONTEND_MARKET_COMPARISON.md`.
 * Ver docs/maps/market-comparison.md.
 *
 * Los errores se deciden siempre por `_httpStatus` + `code`, nunca por el texto de `error`.
 */

const isTrue = (raw) => String(raw ?? '').trim().toLowerCase() === 'true';

/**
 * ¿Comparación de mercado prendida en este build? Solo con
 * `REACT_APP_MARKET_COMPARISON_ENABLED=true` (apagada por defecto). Además, la tarjeta se
 * oculta si `GET /getmarketcomparison` da 404 sin `code` (flag `MARKET_COMPARISON_ENABLED`
 * apagado en la API).
 *
 * @param {string} [raw] Valor de la variable (para tests).
 * @returns {boolean}
 */
export const isMarketComparisonEnabled = (
  raw = process.env.REACT_APP_MARKET_COMPARISON_ENABLED,
) => isTrue(raw);

/*
 * `features.market_comparison` de `GET /getmyrecords` (todas las respuestas 200, también en
 * el espejo del espacio): la ruta está prendida en este entorno. `null` = todavía no se leyó
 * (la tarjeta pide igual y el 404 sin `code` queda de respaldo).
 */
let apiFeature = null;
const featureListeners = new Set();

/** La llama `getMyRecords` con `json.features`. Solo toma booleanos. */
export function noteMarketComparisonFeature(features) {
  const value = features && typeof features === 'object' ? features.market_comparison : undefined;
  if (typeof value !== 'boolean' || value === apiFeature) return;
  apiFeature = value;
  featureListeners.forEach((fn) => fn(value));
}

/** @returns {boolean|null} */
export const getMarketComparisonApiFeature = () => apiFeature;

export function subscribeMarketComparisonFeature(fn) {
  featureListeners.add(fn);
  return () => featureListeners.delete(fn);
}

/** Para tests y al cerrar sesión. */
export function resetMarketComparisonFeature() {
  apiFeature = null;
}

/** `code` estable de un error de la API. Respuestas sin `code` → ''. */
export function readMarketErrorCode(res) {
  const code = res?.code ?? res?.data?.code;
  return typeof code === 'string' ? code.trim().toUpperCase() : '';
}

/** 404 sin `code` = la ruta no existe para el router (flag de la API apagado). */
export function isMarketComparisonOffResponse(res) {
  return res?._httpStatus === 404 && !readMarketErrorCode(res);
}

/**
 * Respuestas que esconden la tarjeta (no son un error para el usuario):
 * 401 sin sesión, 403 `SPACE_FORBIDDEN`, 404 (registro inexistente o flag apagado) y
 * 503 `MARKET_DATA_NOT_MIGRATED` (entorno sin las tablas del piloto).
 */
export function isMarketComparisonHidden(res) {
  const status = res?._httpStatus;
  if (status === 401 || status === 403 || status === 404) return true;
  return status === 503 && readMarketErrorCode(res) === 'MARKET_DATA_NOT_MIGRATED';
}
